import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getAllAttributes,
  createAttribute,
  updateAttribute,
  deleteAttribute,
} from "../services/kpiService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import ConfirmModal from "../components/ui/ConfirmModal";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { resolveRoleName } from "../utils/auth";

const KPIAttributeManagementPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  // State
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [formData, setFormData] = useState({
    attributeValue: "",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  // Delete confirmation
  const [confirmId, setConfirmId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Dropdown
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllAttributes();
      setAttributes(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Failed to load KPI Attributes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered Attributes
  const filteredAttributes = useMemo(() => {
    return attributes.filter((attr) => {
      return (attr.attributeValue || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
  }, [attributes, searchTerm]);

  // Modal handlers
  const openCreateModal = () => {
    setFormData({
      attributeValue: "",
    });
    setFormError(null);
    setModalMode("create");
    setSelectedAttribute(null);
    setShowModal(true);
  };

  const openEditModal = (attr) => {
    setFormData({
      attributeValue: attr.attributeValue || "",
    });
    setFormError(null);
    setModalMode("edit");
    setSelectedAttribute(attr);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAttribute(null);
    setFormError(null);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rawValue = String(formData.attributeValue || "");
    if (!rawValue.trim()) {
      setFormError("Attribute Value is required");
      return;
    }
    if (/[<>]/.test(rawValue)) {
      setFormError("HTML tags are not allowed in Attribute Value");
      return;
    }
    const payload = { attributeValue: rawValue.trim() };

    setSaving(true);
    setFormError(null);
    try {
      if (modalMode === "create") {
        await createAttribute(payload);
        toast.success("KPI Attribute created successfully");
      } else if (modalMode === "edit" && selectedAttribute) {
        await updateAttribute(selectedAttribute.id, payload);
        toast.success("KPI Attribute updated successfully");
      }
      await loadData();
      closeModal();
    } catch (err) {
      toast.error(err.message || "Failed to save KPI Attribute");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setConfirmBusy(true);
    try {
      await deleteAttribute(confirmId);
      toast.success("KPI Attribute deleted successfully");
      await loadData();
    } catch (err) {
      toast.error(err.message || "Failed to delete KPI Attribute");
    } finally {
      setConfirmBusy(false);
      setConfirmId(null);
    }
  };

  const confirmAttrValue = attributes.find((a) => a.id === confirmId)?.attributeValue || "";

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">KPI Attributes</h2>
            <p className="page-subtitle">
              Manage attributes for your KPIs (e.g., Target Value, Threshold)
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search attributes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="secondary-btn sm" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            </div>
            <button className="primary-btn" onClick={openCreateModal}>
              + Create Attribute
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading KPI Attributes...</div>
        ) : (
          <div className="roles-table kpi-admin-table kpi-attribute-table">
            <div className="roles-table-header">
              <div className="cell">ID</div>
              <div className="cell">Attribute Value</div>
              <div className="cell actions">Actions</div>
            </div>
            {filteredAttributes.length === 0 ? (
              <div className="no-results">
                {attributes.length === 0 ? (
                  <>
                    <p>No KPI attributes yet.</p>
                    <button className="primary-btn" onClick={openCreateModal} style={{ marginTop: 8 }}>
                      Create your first Attribute
                    </button>
                  </>
                ) : (
                  "No attributes match your search."
                )}
              </div>
            ) : (
              filteredAttributes.map((attr) => (
                <div
                  key={attr.id}
                  className="roles-table-row"
                >
                  <div className="cell">{attr.id}</div>
                  <div className="cell">{attr.attributeValue}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === attr.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === attr.id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(attr.id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === attr.id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              openEditModal(attr);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              setConfirmId(attr.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === "create" ?
                  "Create New Attribute"
                : "Edit Attribute"}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                {formError && <div className="form-error">{formError}</div>}
                <div className="form-group">
                  <label htmlFor="attributeValue">Attribute Value *</label>
                  <input
                    type="text"
                    id="attributeValue"
                    name="attributeValue"
                    value={formData.attributeValue}
                    onChange={handleInputChange}
                    placeholder="e.g., Target Value: 95"
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={saving}
                  >
                    {saving ?
                      "Saving..."
                    : modalMode === "create" ?
                      "Create"
                    : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmId}
        title="Delete KPI Attribute"
        message={`Are you sure you want to delete "${confirmAttrValue}"? This action cannot be undone.`}
        confirmLabel={confirmBusy ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        isDanger
        onConfirm={handleDelete}
        onCancel={() => setConfirmId(null)}
        busy={confirmBusy}
      />
    </div>
  );
};

export default KPIAttributeManagementPage;

